import type { ReactNode } from 'react';
import { CircularLoading } from '@zawarski/eti-ui';
import { cn } from '@utils/index';

/** Full-height page column: header on top, scrollable body below. */
export const Page = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div
    className={cn(
      'rep:flex rep:h-full rep:w-full rep:flex-col rep:overflow-hidden rep:bg-background',
      className,
    )}>
    {children}
  </div>
);

export const PageBody = ({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) => (
  <div className={cn('rep:min-h-0 rep:flex-1 rep:overflow-y-auto', padded && 'rep:p-4', className)}>
    {children}
  </div>
);

export const Centered = ({ children }: { children: ReactNode }) => (
  <div className='rep:flex rep:h-full rep:w-full rep:items-center rep:justify-center rep:p-6'>
    {children}
  </div>
);

export const PageLoading = ({ message }: { message?: string }) => (
  <Centered>
    <CircularLoading size='md' message={message} />
  </Centered>
);

export const PageError = ({ message = 'Unable to load this page.' }: { message?: string }) => (
  <Centered>
    <p className='rep:text-sm rep:text-destructive'>{message}</p>
  </Centered>
);

export const EmptyState = ({ message }: { message: string }) => (
  <div className='rep:flex rep:w-full rep:items-center rep:justify-center rep:rounded-md rep:bg-muted rep:p-6'>
    <p className='rep:text-sm rep:font-medium rep:text-muted-foreground'>{message}</p>
  </div>
);

/** Label/value pair for read-only view sections. */
export const Field = ({
  label,
  value,
  className,
}: {
  label: string;
  value?: ReactNode;
  className?: string;
}) => (
  <div className={cn('rep:flex rep:min-w-0 rep:flex-col rep:gap-1', className)}>
    <span className='rep:text-sm rep:font-medium rep:text-foreground'>{label}</span>
    <span className='rep:text-sm rep:wrap-break-word rep:text-muted-foreground'>
      {value === undefined || value === null || value === '' ? '—' : value}
    </span>
  </div>
);

/**
 * Palmetto 3.0 group divider (Figma "Hospitals - Sandbox", node 2231:5943): a FieldGrid child
 * that takes the full row, so the fields after it start a new row in the first column. The
 * name (and its optional note) is text screen readers read; the rule is decoration.
 */
export const GroupDivider = ({ name, note }: { name: string; note?: string }) => (
  <div className='rep:col-span-full rep:flex rep:items-center rep:gap-2.5 rep:pt-2 rep:pb-1'>
    <span className='rep:text-xs rep:whitespace-nowrap rep:text-[#515f69]'>
      {name}
      {note ? <span className='rep:text-[#94a3b8]'> · {note}</span> : null}
    </span>
    <span aria-hidden='true' className='rep:h-px rep:flex-1 rep:bg-[#ebebeb]' />
  </div>
);

/** Responsive field grid used inside FormSection cards. */
export const FieldGrid = ({
  children,
  cols = 2,
  className,
}: {
  children: ReactNode;
  cols?: 1 | 2 | 3 | 4;
  className?: string;
}) => (
  <div
    className={cn(
      'rep:grid rep:w-full rep:gap-4 rep:p-4',
      cols === 1 && 'rep:grid-cols-1',
      cols === 2 && 'rep:grid-cols-1 rep:md:grid-cols-2',
      cols === 3 && 'rep:grid-cols-1 rep:md:grid-cols-3',
      cols === 4 && 'rep:grid-cols-1 rep:md:grid-cols-2 rep:xl:grid-cols-4',
      className,
    )}>
    {children}
  </div>
);
